import type { Id, IsoDateTime } from "../primitives";

/**
 * Confirmation calls (#636): who tried to reach the customer about an order,
 * when, and how it went.
 *
 * A row means Call was pressed in the ERP, not that the call connected. The
 * outcome is what the caller says afterwards and is null until they do.
 */
export const CALL_ATTEMPT_OUTCOMES = [
  "no_answer",
  "confirmed",
  "call_back",
] as const;

export type CallAttemptOutcome = (typeof CALL_ATTEMPT_OUTCOMES)[number];

export const CALL_ATTEMPT_OUTCOME_LABELS: Record<CallAttemptOutcome, string> = {
  no_answer: "No answer",
  confirmed: "Confirmed",
  call_back: "Call back",
};

export function isCallAttemptOutcome(
  value: unknown,
): value is CallAttemptOutcome {
  return (
    typeof value === "string" &&
    (CALL_ATTEMPT_OUTCOMES as readonly string[]).includes(value)
  );
}

export interface CallAttempt {
  id: Id;
  orderId: Id;
  contactId: Id | null;
  userId: Id;
  /** Who called, by name; null for a user since removed. */
  userName: string | null;
  startedAt: IsoDateTime;
  outcome: CallAttemptOutcome | null;
  outcomeAt: IsoDateTime | null;
}

const STORE_ZONE = "Asia/Kolkata";

function dayKey(instant: number): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: STORE_ZONE }).format(
    instant,
  );
}

/**
 * "Called 2 times · last today 11:40 by Arun" (#636): what the confirm step
 * says about an order's earlier attempts. Null when there are none. `attempts`
 * is newest first, as the repository returns them.
 */
export function callSummary(
  attempts: readonly CallAttempt[],
  now: number,
): string | null {
  const last = attempts[0];
  if (!last) return null;
  const at = Date.parse(last.startedAt);
  const time = new Intl.DateTimeFormat("en-IN", {
    timeZone: STORE_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(at);
  const day =
    dayKey(at) === dayKey(now)
      ? "today"
      : dayKey(at) === dayKey(now - 86_400_000)
        ? "yesterday"
        : new Intl.DateTimeFormat("en-IN", {
            timeZone: STORE_ZONE,
            day: "numeric",
            month: "short",
          }).format(at);
  const times =
    attempts.length === 1 ? "once" : `${String(attempts.length)} times`;
  const by = last.userName ? ` by ${last.userName}` : "";
  const outcome = last.outcome
    ? ` · ${CALL_ATTEMPT_OUTCOME_LABELS[last.outcome]}`
    : "";
  return `Called ${times} · last ${day} ${time}${by}${outcome}`;
}

/** How long after pressing Call the order still asks how it went. */
export const CALL_OUTCOME_WINDOW_MS = 12 * 60 * 60 * 1000;

/**
 * The attempt to ask "How did it go?" about when someone comes back to the
 * order (#636): `userId`'s newest attempt, if it is the order's newest, has no
 * outcome yet and was started in the last 12 hours. Someone else's call, or
 * one from yesterday, is history, not a question for this person.
 */
export function pendingOutcome(
  attempts: readonly CallAttempt[],
  userId: Id,
  now: number,
): CallAttempt | null {
  const last = attempts[0];
  if (!last || last.userId !== userId || last.outcome !== null) return null;
  const age = now - Date.parse(last.startedAt);
  return age >= 0 && age <= CALL_OUTCOME_WINDOW_MS ? last : null;
}
